import type {
  InstallationType,
  SensorName,
} from "@/base/sensor-configurations"
import type { IconProps } from "@expo/vector-icons/build/createIconSet"
import type React from "react"
import type { SnakeCase } from "type-fest"

import { useStyle } from "react-native-style-utilities"

import {
  Entypo,
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from "@expo/vector-icons"

import { useIsDarkMode } from "../AppThemeContextProvider"
import { COLORS } from "../theme/colors"
import { ICON_SIZES } from "../theme/icon-sizes"
import { getTextColorFromColorScheme } from "../utils"
import {
  DepthSensorSvgIcon,
  FarmSvgIcon,
  LinearMoveIcon,
  PumpSvgIcon,
  ReelSvgIcon,
  ReservoirDepthMonitorSvgIcon,
} from "./CustomIcons"

import type { AppTextProps } from "../Text"

export type AppIconProps = Omit<IconProps<string>, "name"> &
  Pick<AppTextProps, "colorScheme">

export type CustomIconComponent = (props: AppIconProps) => React.JSX.Element

function useThemedProps({
  color,
  colorScheme,
  size,
  style,
  ...rest
}: AppIconProps) {
  const isDark = useIsDarkMode()

  return {
    ...rest,
    style: useStyle(() => {
      let colorFinal = color
      if (typeof colorFinal === "undefined") {
        colorFinal = getTextColorFromColorScheme({
          colorScheme: colorScheme ?? "primary",
          isDark,
        })
      }
      if (typeof colorFinal === "undefined") {
        if (isDark) {
          colorFinal = COLORS.$textLight.primary
        } else {
          colorFinal = COLORS.$text[600]
        }
      }

      return [
        {
          color: colorFinal,
          fontSize: size ?? ICON_SIZES.$md,
        },
        style,
      ]
    }, [color, colorScheme, isDark, size, style]),
  }
}

export const AppIcons = {
  Account: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"account"} {...useThemedProps(props)} />
    )
  },
  Activate: (props: AppIconProps) => {
    return <MaterialIcons name={"check"} {...useThemedProps(props)} />
  },
  Add: (props: AppIconProps) => (
    <MaterialCommunityIcons name={"plus"} {...useThemedProps(props)} />
  ),
  Admin: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"shield-account"}
        {...useThemedProps(props)}
      />
    )
  },
  Analytics: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"chart-areaspline"}
        {...useThemedProps(props)}
      />
    )
  },
  AppPreferences: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        {...useThemedProps(props)}
        name={"keyboard-settings"}
      />
    )
  },
  AppleMaps: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"apple"} {...useThemedProps(props)} />
  },
  Archive: (props: AppIconProps) => {
    return <MaterialIcons name={"archive"} {...useThemedProps(props)} />
  },
  ArrowDown: (props: AppIconProps) => {
    return <MaterialIcons name={"arrow-downward"} {...useThemedProps(props)} />
  },
  ArrowLeft: (props: AppIconProps) => {
    return <MaterialIcons name={"arrow-left"} {...useThemedProps(props)} />
  },
  ArrowRight: (props: AppIconProps) => {
    return <MaterialIcons name={"arrow-right"} {...useThemedProps(props)} />
  },
  ArrowUp: (props: AppIconProps) => {
    return <MaterialIcons name={"arrow-upward"} {...useThemedProps(props)} />
  },
  Battery: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"car-battery"}
        {...useThemedProps(props)}
      />
    )
  },
  Borders: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"border-none-variant"}
        {...useThemedProps(props)}
      />
    )
  },
  CalendarMultipleCheck: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"calendar-multiple-check"}
        {...useThemedProps(props)}
      />
    )
  },
  CalendarSingle: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"calendar"} {...useThemedProps(props)} />
    )
  },

  CheckMark: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"check-circle"}
        {...useThemedProps(props)}
      />
    )
  },
  ChevronDown: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"chevron-down"}
        {...useThemedProps(props)}
      />
    )
  },
  ChevronUp: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"chevron-up"} {...useThemedProps(props)} />
    )
  },
  Clipboard: (props: AppIconProps) => {
    return <MaterialIcons name={"content-copy"} {...useThemedProps(props)} />
  },
  Clock: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"clock"} {...useThemedProps(props)} />
  },
  Close: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"close"} {...useThemedProps(props)} />
  },
  Comment: (props: AppIconProps) => {
    return <MaterialIcons name={"comment"} {...useThemedProps(props)} />
  },
  Compass: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"compass"} {...useThemedProps(props)} />
    )
  },
  Contrast: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"contrast-box"}
        {...useThemedProps(props)}
      />
    )
  },
  Crash: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"bug"} {...useThemedProps(props)} />
  },
  Cross: (props: AppIconProps) => {
    return <Entypo name={"cross"} {...useThemedProps(props)} />
  },
  CustomerService: (props: AppIconProps) => {
    return <MaterialIcons name={"contact-phone"} {...useThemedProps(props)} />
  },
  Database: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"database"} {...useThemedProps(props)} />
    )
  },
  Dev: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"bug"} {...useThemedProps(props)} />
  },
  DeviceActivity: (props: AppIconProps) => {
    return <MaterialIcons name={"timeline"} {...useThemedProps(props)} />
  },
  DeviceCenterPivot: (props: AppIconProps) => {
    return <Ionicons name={"pie-chart-sharp"} {...useThemedProps(props)} />
  },
  DeviceCommandsRemote: (props: AppIconProps) => {
    return (
      <MaterialIcons name={"settings-remote"} {...useThemedProps(props)} />
    )
  },
  DeviceConfiguration: (props: AppIconProps) => {
    return <Ionicons name={"settings"} {...useThemedProps(props)} />
  },
  DeviceConnection: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"vector-link"}
        {...useThemedProps(props)}
      />
    )
  },
  DeviceGeneric: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"router-wireless-settings"}
        {...useThemedProps(props)}
      />
    )
  },
  DeviceLinearMove: () => {
    return (
      <LinearMoveIcon />
      // <MaterialCommunityIcons name="water-pump" {...useThemedProps(props)} />
    )
  },

  DeviceOffline: (props: AppIconProps) => (
    <MaterialCommunityIcons
      name={"cloud-off-outline"}
      {...useThemedProps(props)}
    />
  ),

  DevicePrototype: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"robot-industrial"}
        {...useThemedProps(props)}
      />
    )
  },

  DevicePump: ({ color }: AppIconProps) => {
    const isDark = useIsDarkMode()
    // TODO: Allow override for custom icon props
    if (typeof color === "undefined") {
      color = isDark ? COLORS.$textLight.primary : COLORS.$textDark.primary
    }
    return (
      <PumpSvgIcon
        fill={color}
        stroke={color}
      />
    )
  },

  DevicePumpVfd: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"chart-areaspline"}
        {...useThemedProps(props)}
      />
    )
  },

  DeviceReel: () => {
    return (
      <ReelSvgIcon
      // {...useThemedProps(props)}
      />
    )
  },

  DeviceReservoirDepthMonitor: () => {
    return <ReservoirDepthMonitorSvgIcon />
  },

  DeviceTravelerSoft: (props: AppIconProps) => {
    return (
      // <CenterPivotSvgIcon/>
      <MaterialCommunityIcons
        {...useThemedProps(props)}
        name={"arrow-right-bold-box"}
      />
    )
  },
  DeviceValve: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"valve"} {...useThemedProps(props)} />
  },
  Disabled: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"close"} {...useThemedProps(props)} />
  },

  Disconnect: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"lan-disconnect"}
        {...useThemedProps(props)}
      />
    )
  },

  Dot: (props: AppIconProps) => {
    return <Entypo name={"dot-single"} {...useThemedProps(props)} />
  },
  DotsThreeHorizontal: (props: AppIconProps) => {
    return <Entypo name={"dots-three-horizontal"} {...useThemedProps(props)} />
  },
  DotsThreeVertical: (props: AppIconProps) => {
    return <Entypo name={"dots-three-vertical"} {...useThemedProps(props)} />
  },
  Download: (props: AppIconProps) => {
    return <MaterialIcons name={"cloud-download"} {...useThemedProps(props)} />
  },
  ExcelExport: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"microsoft-excel"}
        {...useThemedProps(props)}
      />
    )
  },
  Draw: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"draw"} {...useThemedProps(props)} />
  },
  Edit: (props: AppIconProps) => {
    return <MaterialIcons name={"edit"} {...useThemedProps(props)} />
  },
  Error: (props: AppIconProps) => {
    return <MaterialIcons name={"error"} {...useThemedProps(props)} />
  },
  ExpandLess: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"chevron-up"} {...useThemedProps(props)} />
    )
  },

  ExpandMore: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"chevron-down"}
        {...useThemedProps(props)}
      />
    )
  },

  Farm: () => {
    // return <MaterialCommunityIcons name="home" {...useThemedProps(props)} />
    return <FarmSvgIcon />
  },

  FarmJoinCode: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"account-plus"}
        {...useThemedProps(props)}
      />
    )
  },

  Feedback: (props: AppIconProps) => {
    return <MaterialIcons name={"feedback"} {...useThemedProps(props)} />
  },
  Field: (props: AppIconProps) => {
    return <MaterialIcons name={"view-quilt"} {...useThemedProps(props)} />
  },
  FlowOff: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"water-boiler-off"}
        {...useThemedProps(props)}
      />
    )
  },
  FlowOn: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"water-boiler"}
        {...useThemedProps(props)}
      />
    )
  },
  Gauge: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"gauge"} {...useThemedProps(props)} />
  },
  GaugeEmpty: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"gauge-empty"}
        {...useThemedProps(props)}
      />
    )
  },
  GaugeFull: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"gauge-full"} {...useThemedProps(props)} />
    )
  },
  GaugeLow: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"gauge-low"} {...useThemedProps(props)} />
    )
  },
  GoogleMaps: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"google-maps"}
        {...useThemedProps(props)}
      />
    )
  },
  GpsFixed: (props: AppIconProps) => {
    return <MaterialIcons name={"gps-fixed"} {...useThemedProps(props)} />
  },
  GpsOff: (props: AppIconProps) => {
    return <MaterialIcons name={"gps-off"} {...useThemedProps(props)} />
  },
  Height: (props: AppIconProps) => {
    return <MaterialIcons name={"height"} {...useThemedProps(props)} />
  },
  Help: (props: AppIconProps) => {
    return <MaterialIcons name={"help"} {...useThemedProps(props)} />
  },
  Humidity: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"weather-cloudy"}
        {...useThemedProps(props)}
      />
    )
  },
  History: (props: AppIconProps) => {
    return <MaterialIcons name={"history"} {...useThemedProps(props)} />
  },
  Info: (props: AppIconProps) => {
    return <MaterialIcons name={"info"} {...useThemedProps(props)} />
  },
  LikeNo: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"thumb-down"} {...useThemedProps(props)} />
    )
  },
  LikeYes: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"thumb-up"} {...useThemedProps(props)} />
    )
  },
  Link: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"link"} {...useThemedProps(props)} />
  },
  ListItemEnd: (props: AppIconProps) => {
    return <Entypo name={"chevron-right"} {...useThemedProps(props)} />
  },
  LocationPinned: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"pin"} {...useThemedProps(props)} />
  },
  LocationUnpinned: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"pin-off"} {...useThemedProps(props)} />
    )
  },
  Magnet: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"magnet"} {...useThemedProps(props)} />
    )
  },
  MagnetOn: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"magnet-on"} {...useThemedProps(props)} />
    )
  },
  ManageUsers: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"account-group"}
        {...useThemedProps(props)}
      />
    )
  },
  MapMarker: (props: AppIconProps) => {
    return <MaterialIcons name={"place"} {...useThemedProps(props)} />
  },
  Measurement: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"ruler"} {...useThemedProps(props)} />
  },
  Menu: (props: AppIconProps) => {
    return <MaterialIcons name={"menu"} {...useThemedProps(props)} />
  },
  NotificationsOff: (props: AppIconProps) => {
    return <Ionicons name={"notifications-off"} {...useThemedProps(props)} />
  },
  NotificationsOn: (props: AppIconProps) => {
    return <Ionicons name={"notifications"} {...useThemedProps(props)} />
  },
  Permissions: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"shield-account"}
        {...useThemedProps(props)}
      />
    )
  },
  Phone: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"phone"} {...useThemedProps(props)} />
  },
  Print: (props: AppIconProps) => {
    return <MaterialIcons name={"print"} {...useThemedProps(props)} />
  },
  PrivacyPolicy: (props: AppIconProps) => {
    return <MaterialIcons name={"privacy-tip"} {...useThemedProps(props)} />
  },
  PumpOff: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"water-pump-off"}
        {...useThemedProps(props)}
      />
    )
  },
  PumpOn: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"water-pump"} {...useThemedProps(props)} />
    )
  },
  ReelRun: (props: AppIconProps) => {
    return <Entypo name={"flow-line"} {...useThemedProps(props)} />
  },
  Refresh: (props: AppIconProps) => {
    return <MaterialIcons name={"refresh"} {...useThemedProps(props)} />
  },
  Repeat: (props: AppIconProps) => {
    return <MaterialIcons name={"repeat"} {...useThemedProps(props)} />
  },
  Restart: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"restart"} {...useThemedProps(props)} />
    )
  },
  RotateClockwise: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"rotate-right"} {...useThemedProps(props)} />
    )
  },
  RotateCounterClockwise: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"rotate-left"} {...useThemedProps(props)} />
    )
  },
  Sensor: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"smoke-detector-variant"}
        {...useThemedProps(props)}
      />
    )
  },
  SensorDepth: () => {
    return <DepthSensorSvgIcon />
  },
  Settings: (props: AppIconProps) => {
    return <MaterialIcons name={"settings"} {...useThemedProps(props)} />
  },
  Share: (props: AppIconProps) => (
    <MaterialCommunityIcons name={"share"} {...useThemedProps(props)} />
  ),
  SignOut: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"logout"} {...useThemedProps(props)} />
    )
  },
  Signal: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"signal-distance-variant"}
        {...useThemedProps(props)}
      />
    )
  },
  SineWave: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"sine-wave"} {...useThemedProps(props)} />
    )
  },
  SleepOff: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"sleep-off"} {...useThemedProps(props)} />
    )
  },
  SleepOn: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"sleep"} {...useThemedProps(props)} />
  },
  Sms: (props: AppIconProps) => {
    return <MaterialIcons name={"sms"} {...useThemedProps(props)} />
  },
  SortAlphabetical: (props: AppIconProps) => {
    return <MaterialIcons name={"sort-by-alpha"} {...useThemedProps(props)} />
  },
  SortAscending: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"sort-ascending"}
        {...useThemedProps(props)}
      />
    )
  },
  SortDescending: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"sort-descending"}
        {...useThemedProps(props)}
      />
    )
  },
  Speed: (props: AppIconProps) => {
    return <MaterialIcons name={"speed"} {...useThemedProps(props)} />
  },
  StatusMap: (props: AppIconProps) => {
    return <MaterialIcons name={"map"} {...useThemedProps(props)} />
  },
  StepForward: (props: AppIconProps) => {
    return <MaterialIcons name={"forward"} {...useThemedProps(props)} />
  },

  Success: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"check-circle"}
        {...useThemedProps(props)}
      />
    )
  },
  Support: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"lifebuoy"} {...useThemedProps(props)} />
    )
  },
  SupportDocumentation: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"book"} {...useThemedProps(props)} />
  },
  SwapHorizontal: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"swap-horizontal"}
        {...useThemedProps(props)}
      />
    )
  },
  SwapVertical: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"swap-vertical"}
        {...useThemedProps(props)}
      />
    )
  },
  SwitchClosed: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"electric-switch-closed"}
        {...useThemedProps(props)}
      />
    )
  },
  SwitchOpen: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"electric-switch"}
        {...useThemedProps(props)}
      />
    )
  },
  SystemUpdate: (props: AppIconProps) => {
    return <MaterialIcons name={"system-update"} {...useThemedProps(props)} />
  },
  Target: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"target"} {...useThemedProps(props)} />
    )
  },
  Temperature: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"thermometer"}
        {...useThemedProps(props)}
      />
    )
  },

  TermsOfService: (props: AppIconProps) => {
    return <MaterialIcons name={"gavel"} {...useThemedProps(props)} />
  },
  ThemeLightDark: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"theme-light-dark"}
        {...useThemedProps(props)}
      />
    )
  },
  Ticket: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"ticket"} {...useThemedProps(props)} />
    )
  },
  TimeTable: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"timetable"} {...useThemedProps(props)} />
    )
  },
  Timeline: (props: AppIconProps) => {
    return <MaterialIcons name={"timeline"} {...useThemedProps(props)} />
  },
  Timer10: (props: AppIconProps) => {
    return <MaterialIcons name={"timer-10"} {...useThemedProps(props)} />
  },
  TrashCan: (props: AppIconProps) => {
    return <MaterialIcons name={"delete"} {...useThemedProps(props)} />
  },
  Truck: (props: AppIconProps) => {
    return <MaterialCommunityIcons name={"truck"} {...useThemedProps(props)} />
  },
  UnderConstruction: (props: AppIconProps) => {
    return <MaterialIcons name={"construction"} {...useThemedProps(props)} />
  },
  Undo: (props: AppIconProps) => {
    return <MaterialIcons name={"undo"} {...useThemedProps(props)} />
  },
  User: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"account"} {...useThemedProps(props)} />
    )
  },
  ValveClosed: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"valve-closed"}
        {...useThemedProps(props)}
      />
    )
  },
  ValveOpen: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"valve-open"} {...useThemedProps(props)} />
    )
  },
  VerifyPhoneNumber: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"cellphone-check"}
        {...useThemedProps(props)}
      />
    )
  },
  VisibilityOff: (props: AppIconProps) => {
    const themedProps = useThemedProps(props)

    return <MaterialIcons name={"visibility-off"} {...themedProps} />
  },
  VisibilityOn: (props: AppIconProps) => {
    return <MaterialIcons name={"visibility"} {...useThemedProps(props)} />
  },
  VoltageBolt: (props: AppIconProps) => {
    return <MaterialIcons name={"bolt"} {...useThemedProps(props)} />
  },
  Warning: (props: AppIconProps) => {
    return <MaterialIcons name={"warning"} {...useThemedProps(props)} />
  },
  Spray: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons name={"spray"} {...useThemedProps(props)} />
    )
  },
  Water: (props: AppIconProps) => {
    return <Ionicons name={"water"} {...useThemedProps(props)} />
  },
  Wheel: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"axis-x-rotate-clockwise"}
        {...useThemedProps(props)}
      />
    )
  },
  ZoomIn: (props: AppIconProps) => {
    return (
      <MaterialCommunityIcons
        name={"magnify-plus-outline"}
        {...useThemedProps(props)}
      />
    )
  },
  ZoomOutMap: (props: AppIconProps) => {
    return <MaterialIcons name={"zoom-out-map"} {...useThemedProps(props)} />
  },
}

export type IconProp =
  | CustomIconComponent
  | React.JSX.Element
  | keyof typeof AppIcons
  | null

/**
 * Get the icon for a given sensor name.
 */
export function getIconForSensor(
  sensorName: SensorName | SnakeCase<SensorName>,
): CustomIconComponent {
  switch (sensorName) {
    case "depth": {
      return AppIcons.SensorDepth
    }
    case "flow": {
      return AppIcons.FlowOn
    }
    case "vfd": {
      return AppIcons.DevicePumpVfd
    }
    case "pressure_switch":
    case "pressureSwitch":
    case "pressure": {
      return AppIcons.PumpOn
    }
    case "reel": {
      return AppIcons.Wheel
    }
    case "wheel": {
      return AppIcons.Wheel
    }
    case "battery": {
      return AppIcons.Battery
    }
    case "gps": {
      return AppIcons.GpsFixed
    }
    case "hall_switch":
    case "hallSwitch": {
      return AppIcons.MagnetOn
    }
    case "humidity": {
      return AppIcons.Humidity
    }
    case "icm": {
      return AppIcons.Compass
    }
    case "relay": {
      return AppIcons.SwitchClosed
    }
    case "temperature": {
      return AppIcons.Temperature
    }
    default: {
      return AppIcons.Sensor
    }
  }
}

/**
 * Get the icon for a given installation type.
 */
export function getIconForInstallationType(
  installationType: InstallationType,
): CustomIconComponent {
  switch (installationType) {
    case "valve": {
      return AppIcons.DeviceValve
    }
    case "reel":
    case "reel_with_booster_off_only": {
      return AppIcons.DeviceReel
    }
    case "center_pivot": {
      return AppIcons.DeviceCenterPivot
    }
    case "linear_move": {
      return AppIcons.DeviceLinearMove
    }
    case "traveller_soft": {
      return AppIcons.DeviceTravelerSoft
    }
    case "prototype": {
      return AppIcons.DevicePrototype
    }
    case "pump":
    case "pump_off_only":
    case "pump_on_off":
    case "pump_vfd": {
      return AppIcons.DevicePump
    }
    case "unconfigured": {
      return AppIcons.Warning
    }
    default: {
      return AppIcons.DeviceGeneric
    }
  }
}
